// === PlanCreatePage.jsx ===
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box, Button, Card, Grid, InputAdornment,
    Stack, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { handleErrorMessages } from 'src/pages/components/errorhanling-formating';
import { paths } from 'src/routes/paths';
import { getRequest, postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import * as Yup from 'yup';
import PlansCreateCards from './plans-create-form-cards';

export const PlanCreatePage = ({ currentItem }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [ loading, setLoading ] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const modules = location.state;

    const [ selectedFeatures, setSelectedFeatures ] = useState({});
    const [ featureCosts, setFeatureCosts ] = useState({});
    const [ fixedValues, setFixedValues ] = useState({});

    const handleFeatureSelection = (moduleId) => (featureIds, moduleCost) => {
        setSelectedFeatures((prev) => ({ ...prev, [ moduleId ]: featureIds }));
        setFeatureCosts((prev) => ({ ...prev, [ moduleId ]: moduleCost }));
    };

    const NewDocumentSchema = Yup.object().shape({});

    const defaultValues = useMemo(() => ({}), [ currentItem ]);

    const methods = useForm({ resolver: yupResolver(NewDocumentSchema), defaultValues });

    useEffect(() => {
        getFixedValues();
    }, []);

    const getFixedValues = async () => {
        setLoading(true);
        const data = await getRequest(endpoints.admin_dashboard.packages_plans.plans.get_fixed_values);
        if (data) setFixedValues(data);
        setLoading(false);
    };

    const { reset, handleSubmit } = methods;

    useEffect(() => {
        reset(defaultValues);
    }, [ currentItem, reset, defaultValues ]);


    const onSubmit = handleSubmit(async (data) => {
        setLoading(true);
        const formData = new FormData();

        formData.append('plan_name', data.plan_name);
        formData.append('total_trial_days', data.total_trial_days);
        formData.append('discount_percentage', data.discount_percentage);
        formData.append('fixed_setup_cost', data.fixed_setup_cost);
        formData.append('total_plan_cost', data.total_plan_cost);
        formData.append('description', data.description);

        formData.append('old_data_storage_cost', data.old_data_storage_cost || 0);
        formData.append('storage_cost', data.storage_cost || 0);
        formData.append('project_limit', data.project_limit || 0);
        formData.append('project_user_limit', data.project_user_limit || 0);
        formData.append('employee_limit', data.employee_limit || 0);

        const allSelectedFeatureIds = Object.values(selectedFeatures).flat();
        allSelectedFeatureIds.forEach((id, index) => {
            formData.append(`product_features[${index}]`, id);
        });

        // // If you have any hardcoded or additional addon logic:
        // formData.append('product_addons[0]', 3); // example static addon

        try {
            const response = await postRequest(
                currentItem?.id
                    ? `${endpoints.admin_dashboard.packages_plans.plans.post_list}/${currentItem.id}?_method=PUT`
                    : endpoints.admin_dashboard.packages_plans.plans.post_list,
                formData,
                true
            );

            if (response.metadata?.success) {
                reset();
                navigate('/dashboard/product-packages/plans');
                enqueueSnackbar({ variant: 'success', message: response.metadata.message });
            } else {
                handleErrorMessages(response?.metadata?.message || 'An error occurred.');
            }
        } catch (err) {
            handleErrorMessages(err?.metadata?.message || 'Submission failed.');
        } finally {
            setLoading(false);
        }
    });


    const [ inputs, setInputs ] = useState({
        storage_cost: 0,
        project_limit: 0,
        project_user_limit: 0,
        employee_limit: 0
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputs(prev => ({ ...prev, [ name ]: Number(value) }));
    };

    const rows = [
        { name: 'Store Old Project Data', apiValue: fixedValues?.storage_cost || 0, inputValue: inputs?.storage_cost || 0 },
        { name: 'Number of Projects', apiValue: fixedValues?.project_limit || 0, inputValue: inputs?.project_limit || 0 },
        { name: 'Number of Project Users', apiValue: fixedValues?.project_user_limit || 0, inputValue: inputs?.project_user_limit || 0 },
        { name: 'Number of Employees', apiValue: fixedValues?.employee_limit || 0, inputValue: inputs?.employee_limit || 0 }
    ];

    const total = rows.reduce((acc, row) => acc + row.apiValue * row.inputValue, 0);
    const featureCostTotal = Object.values(featureCosts).reduce((acc, cost) => acc + cost, 0);
    const grandTotal = total + featureCostTotal;

    useEffect(() => {
        methods.setValue('total_plan_cost', grandTotal.toFixed(2));
    }, [ grandTotal ]);

    return (
        <Card sx={ { padding: '20px' } }>
            <FormProvider methods={ methods } onSubmit={ onSubmit }>
                <CustomBreadcrumbs
                    heading="Product / Packages Plan"
                    links={ [ { name: '', href: paths.dashboard.root } ] }
                    action={
                        <Button
                            onClick={ () => navigate('/dashboard/product-packages/plans') }
                            variant="contained"
                            startIcon={ <Iconify icon="material-symbols:arrow-back-ios-rounded" /> }
                        >
                            Back
                        </Button>
                    }
                    sx={ { mb: { xs: 3, md: 2 } } }
                />

                <Grid xs={ 3 }>
                    <Box display="grid" gridTemplateColumns={ { xs: '1fr', md: 'repeat(3, 1fr)' } } gap={ 2 }>
                        <RHFTextField name="plan_name" label="Plan Name" />
                        <RHFTextField name="total_trial_days" label="Trial Days" type="number" />
                        <RHFTextField name="discount_percentage" label="Discount %" type="number" />
                        <RHFTextField name="fixed_setup_cost" label="Fixed Setup Cost" type="number" />
                        <RHFTextField name="total_plan_cost" label="Total Plan Cost" type="number" />
                    </Box>
                    <Box mt={ 3 }><RHFTextField name="description" label="Description" multiline rows={ 4 } /></Box>
                </Grid>

                <Grid xs={ 12 } mt={ 3 } sx={ { padding: '20px' } }>
                    <Typography variant="h6">Fixed Costs</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Feature</TableCell>
                                <TableCell>Value</TableCell>
                                <TableCell>Input</TableCell>
                                <TableCell>Calculated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { rows.map((row, index) => (
                                <TableRow key={ index }>
                                    <TableCell>{ row.name }</TableCell>
                                    <TableCell>{ row.apiValue }</TableCell>
                                    <TableCell>
                                        <TextField
                                            name={ Object.keys(inputs)[ index ] }
                                            type="number"
                                            value={ row.inputValue }
                                            onChange={ handleChange }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>${ (row.apiValue * row.inputValue).toFixed(2) }</TableCell>
                                </TableRow>
                            )) }
                            <TableRow><TableCell colSpan={ 3 }>Total Fixed Cost</TableCell><TableCell>${ total.toFixed(2) }</TableCell></TableRow>
                            <TableRow><TableCell colSpan={ 3 }>Total Feature Cost</TableCell><TableCell>${ featureCostTotal.toFixed(2) }</TableCell></TableRow>
                            <TableRow><TableCell colSpan={ 3 }>Grand Total</TableCell><TableCell>${ grandTotal.toFixed(2) }</TableCell></TableRow>
                        </TableBody>
                    </Table>
                </Grid>

                <Stack mt={ 3 }><Typography variant="h6">Plan Features</Typography></Stack>
                { modules?.map((module) => (
                    <PlansCreateCards
                        key={ module.id }
                        plan_name={ module.module_name }
                        features={ module.feature_list }
                        onFeatureSelectChange={ handleFeatureSelection(module.id) }
                    />
                )) }

                <Box mt={ 3 } display="flex" justifyContent="flex-end">
                    <Button type="submit" variant="contained" disabled={ loading }>
                        { loading ? 'Submitting...' : 'Submit Plan' }
                    </Button>
                </Box>
            </FormProvider>
        </Card>
    );
};